import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Instagram } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_b8d07c5d-fcca-4dc8-ad54-fc1da9664427/artifacts/71fm56iq_LOGO.png';

const Footer = () => {
  const { t } = useLanguage();

  const quickLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/about', label: t('nav.about') },
    { path: '/services', label: t('nav.services') },
    { path: '/get-in-touch', label: t('nav.getInTouch') },
    { path: '/contact', label: t('nav.contact') },
  ];

  return (
    <footer className="bg-navy-deep text-white" data-testid="footer">
      <div className="container-main py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="space-y-6">
            <Link to="/" aria-label="EuroFortaleza Home">
              <img 
                src={LOGO_URL} 
                alt="EuroFortaleza Logo" 
                className="h-16 w-auto"
              />
            </Link>
            <p className="text-gray-300 text-sm leading-relaxed">
              {t('footer.description')}
            </p>
            <div className="flex items-center space-x-4">
              <a 
                href="https://www.instagram.com/eurofortaleza/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-gold-main transition-colors"
                aria-label="Follow us on Instagram"
                data-testid="footer-instagram"
              >
                <Instagram className="w-6 h-6" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-alatsi text-lg mb-6 text-gold-main">
              {t('footer.quickLinks')}
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-gray-300 hover:text-white transition-colors text-sm"
                    data-testid={`footer-link-${link.path.replace('/', '') || 'home'}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-alatsi text-lg mb-6 text-gold-main">
              {t('footer.contactInfo')}
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-gold-main flex-shrink-0 mt-0.5" />
                <div className="text-sm text-gray-300">
                  <p>Marcelo Junior: +44 7708 773091</p>
                  <p>Marcelo Botelho: +44 7842485139</p>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-gold-main flex-shrink-0 mt-0.5" />
                <a 
                  href="mailto:eurofortaleza2025@gmail.com" 
                  className="text-sm text-gray-300 hover:text-white transition-colors"
                  data-testid="footer-email"
                >
                  eurofortaleza2025@gmail.com
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-gold-main flex-shrink-0 mt-0.5" />
                <address className="text-sm text-gray-300 not-italic">
                  20 Elm Road, Thornton Heath<br />
                  Croydon, London CR78RH<br />
                  United Kingdom
                </address>
              </li>
            </ul>
          </div>

          {/* Business Hours */}
          <div>
            <h4 className="font-alatsi text-lg mb-6 text-gold-main">
              {t('footer.businessHours')}
            </h4>
            <div className="flex items-start space-x-3">
              <Clock className="w-5 h-5 text-gold-main flex-shrink-0 mt-0.5" />
              <div className="text-sm text-gray-300">
                <p className="font-semibold text-white">{t('footer.hours')}</p>
                <p>{t('footer.hoursTime')}</p>
                <p className="mt-2">{t('footer.sunday')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="container-main py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">
              {t('footer.copyright')}
            </p>
            <p className="text-gold-main text-sm font-semibold">
              {t('footer.tagline')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
